export type AuthMethod = 'supabase' | 'workos';

/**
 * Determine which authentication method to use based on email domain
 */
export const determineAuthMethod = (email: string): AuthMethod => {
  // Enterprise emails use WorkOS SSO
  if (isEnterpriseEmail(email)) {
    return 'workos';
  }
  // Everyone else uses Supabase
  return 'supabase';
};

/**
 * Check if email is from an enterprise domain
 */
export const isEnterpriseEmail = (email: string): boolean => {
  const enterpriseDomains = ['kajeet.com'];
  return enterpriseDomains.some(domain => email.endsWith(`@${domain}`));
};

/**
 * Admin emails that can always use password backdoor
 */
const ADMIN_BACKDOOR_EMAILS = [
  'cfrancosky@kajeet.com',
  'dpinto@kajeet.com',
  // Add other admin emails as needed
];

/**
 * Check if user can use backdoor password access
 */
export const canUseBackdoorAccess = (email: string): boolean => {
  return ADMIN_BACKDOOR_EMAILS.includes(email);
};

/**
 * Generate callback URL for current environment
 */
export const getCallbackUrl = (): string => {
  // For Vercel deployment - use /auth/callback path as expected by WorkOS
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/auth/callback`;
  }
  
  // Fallback for server-side rendering
  return `${process.env.REACT_APP_BASE_URL || 'https://ideahopper.kajeet.cloud'}/auth/callback`;
};

/**
 * Validate state parameter from WorkOS callback
 */
export const validateAuthState = (state: string): { authMethod: string; timestamp: number } | null => {
  try {
    const parsed = JSON.parse(state);
    if (parsed.authMethod === 'workos' && typeof parsed.timestamp === 'number') {
      // Check if state is not too old (5 minutes max)
      const age = Date.now() - parsed.timestamp;
      if (age > 5 * 60 * 1000) {
        console.warn('Auth state is too old');
        return null;
      }
      return parsed;
    }
  } catch (error) {
    console.error('Failed to parse auth state:', error);
  }
  return null;
};
